"use client";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { apiFetchWithRefresh } from "@/lib/api/core";
import * as Ably from "ably";
import { LiveObjects } from "ably/liveobjects";
import { AblyProvider as AProvider } from "ably/react";

import { useEffect, useMemo, useReducer, type JSX } from "react";
import { canUseAbly } from "@/utils/common/region";
import {
  isLoggingOut,
  isSessionExpired,
  SESSION_EXPIRED_EVENT,
  LOGOUT_STARTED_EVENT,
} from "@/utils/common/logout";

export default function AblyProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>): JSX.Element {
  const { user } = useAuthContext();
  const userId = user?.id;
  const ablyEnabled = canUseAbly();
  const [clientVersion, replaceClient] = useReducer((version: number) => version + 1, 0);
  // Keep context available to both existing and newly mounted consumers after
  // terminal expiry. Closed clients cannot authorize, but missing context
  // would make Ably's descendant hooks throw.
  const entry = useMemo(
    () => (ablyEnabled && userId ? getAblyClient(userId) : null),
    [ablyEnabled, userId, clientVersion],
  );

  useEffect(() => {
    if (!entry) return;

    // Activity can replay this effect after cleanup has closed the client,
    // while preserving the memo and descendant component state.
    if (entry.closed && !isLoggingOut() && !isSessionExpired()) {
      replaceClient();
      return;
    }

    entry.consumers += 1;
    const onSessionStopped = () => closeClient(entry);
    const onPageShow = () => {
      if (entry.closed && !isLoggingOut() && !isSessionExpired()) replaceClient();
    };
    globalThis.addEventListener(SESSION_EXPIRED_EVENT, onSessionStopped);
    globalThis.addEventListener(LOGOUT_STARTED_EVENT, onSessionStopped);
    globalThis.addEventListener("pagehide", onSessionStopped);
    globalThis.addEventListener("pageshow", onPageShow);
    if (isLoggingOut() || isSessionExpired()) {
      closeClient(entry);
    } else if (!entry.closed && entry.client.connection.state === "initialized") {
      entry.client.connect();
    }

    return () => {
      globalThis.removeEventListener(SESSION_EXPIRED_EVENT, onSessionStopped);
      globalThis.removeEventListener(LOGOUT_STARTED_EVENT, onSessionStopped);
      globalThis.removeEventListener("pagehide", onSessionStopped);
      globalThis.removeEventListener("pageshow", onPageShow);
      entry.consumers -= 1;
      // Allow Strict Mode's effect replay and same-commit handoffs to retain
      // the connection. A single unmount must not disconnect other consumers.
      queueMicrotask(() => {
        if (entry.consumers === 0) closeClient(entry);
      });
    };
  }, [entry]);

  if (!entry) {
    return <>{children}</>;
  }

  return <AProvider client={entry.client}>{children}</AProvider>;
}

type SharedClient = {
  client: Ably.Realtime;
  userId: string;
  consumers: number;
  closed: boolean;
};

let globalClient: SharedClient | null = null;

function closeClient(entry: SharedClient): void {
  if (entry.closed) return;
  entry.closed = true;
  if (globalClient === entry) globalClient = null;
  entry.client.close();
}

function getAblyClient(userId: string): SharedClient {
  const isBrowser = globalThis.window !== undefined;
  const stopped = isBrowser && (isLoggingOut() || isSessionExpired());
  if (isBrowser && !stopped && globalClient?.userId === userId) return globalClient;

  const clientId = encodeURIComponent(userId || "genericUser");
  const entry: SharedClient = {
    client: new Ably.Realtime({
      authCallback: async (_data, callback) => {
        // Check inside the request thunk too: a user can leave or change while
        // the shared refresh is pending, before it retries this request.
        const assertActive = () => {
          if (
            entry.closed ||
            globalClient !== entry ||
            entry.consumers === 0 ||
            isLoggingOut() ||
            isSessionExpired()
          ) {
            closeClient(entry);
            throw new Error("Ably auth stopped: session is no longer active");
          }
        };
        try {
          const res = await apiFetchWithRefresh(() => {
            assertActive();
            return fetch(`${globalThis.location.origin}/api/ably/${clientId}`, {
              method: "POST",
            });
          });
          assertActive();
          if (!res.ok) {
            callback(`Ably auth failed: ${res.status}`, null);
            return;
          }
          const tokenRequest = await res.json();
          assertActive();
          callback(null, tokenRequest);
        } catch (err) {
          callback(String(err), null);
        }
      },
      autoConnect: false,
      recover: (_, cb) => {
        cb(true);
      },
      plugins: { LiveObjects },
    }),
    userId,
    consumers: 0,
    closed: false,
  };
  // Server renders need real context, but must never share clients across
  // requests. autoConnect keeps these render-local clients disconnected.
  // Late mounts during expiry/logout get a closed context for their hooks.
  if (stopped) closeClient(entry);
  else if (isBrowser) globalClient = entry;
  return entry;
}
